import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useState } from 'react';
import { RgbaColorPicker } from 'react-colorful';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';

// Type to represent RGBA color
export interface RGBAColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

interface ColorPickerRGBAProps {
  color: RGBAColor;
  onChange: (color: RGBAColor) => void;
  label?: string;
}

export const ColorPickerRGBA: React.FC<ColorPickerRGBAProps> = ({
  color,
  onChange,
  label
}) => {
  useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  // Convert RGBA to CSS color string
  const getRgbaString = (color: RGBAColor) => {
    return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
  };

  // Helper to parse CSS RGBA string to color object
  const parseRgbaString = (rgbaString: string): RGBAColor => {
    try {
      const matches = rgbaString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
      if (matches) {
        return {
          r: parseInt(matches[1], 10),
          g: parseInt(matches[2], 10),
          b: parseInt(matches[3], 10),
          a: matches[4] !== undefined ? parseFloat(matches[4]) : 1
        };
      }
    } catch (error) {
      console.error("Error parsing RGBA string:", error);
    }
    return { r: 0, g: 0, b: 0, a: 1 };
  };

  return (
    <div className="flex flex-col gap-2">
      {label && <Label className="text-sm font-medium">{label}</Label>}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button 
            type="button"
            className="flex items-center gap-2.5 cursor-pointer group"
          >
            <div 
              className="h-8 w-8 rounded-lg border border-[#E5E7EB] shadow-sm transition-all duration-200 group-hover:shadow-md group-hover:scale-105" 
              style={{ backgroundColor: getRgbaString(color) }} 
            />
            <span className="text-[12px] text-[#6B7280] group-hover:text-[#4B5563] transition-colors">
              {getRgbaString(color)}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3">
          <div className="space-y-4">
            <RgbaColorPicker color={color} onChange={onChange} />
            
            <div className="space-y-2">
              <Label>{translateInterfaceText("Opacidade")}</Label>
              <Slider
                min={0}
                max={1}
                step={0.01}
                value={[color.a]}
                onValueChange={([value]) => {
                  onChange({ ...color, a: value });
                }}
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>{translateInterfaceText("Transparente")}</span>
                <span>{translateInterfaceText("Opaco")}</span>
              </div>
            </div>

            {/* Compact RGBA inputs in single row */}
            <div className="pt-2">
              <div className="grid grid-cols-8 gap-2 items-center">
                <div className="text-xs text-gray-500">R:</div>
                <input 
                  type="number" 
                  min="0" 
                  max="255" 
                  value={color.r} 
                  onChange={(e) => onChange({...color, r: parseInt(e.target.value) || 0})}
                  className="col-span-1 w-full p-1 text-sm border rounded"
                />
                
                <div className="text-xs text-gray-500">G:</div>
                <input 
                  type="number" 
                  min="0" 
                  max="255" 
                  value={color.g} 
                  onChange={(e) => onChange({...color, g: parseInt(e.target.value) || 0})}
                  className="col-span-1 w-full p-1 text-sm border rounded"
                />
                
                <div className="text-xs text-gray-500">B:</div>
                <input 
                  type="number" 
                  min="0" 
                  max="255" 
                  value={color.b} 
                  onChange={(e) => onChange({...color, b: parseInt(e.target.value) || 0})}
                  className="col-span-1 w-full p-1 text-sm border rounded"
                />
                
                <div className="text-xs text-gray-500">A:</div>
                <input 
                  type="number" 
                  min="0" 
                  max="1" 
                  step="0.01" 
                  value={color.a.toFixed(2)} 
                  onChange={(e) => onChange({...color, a: parseFloat(e.target.value) || 0})}
                  className="col-span-1 w-full p-1 text-sm border rounded"
                />
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Utility functions to work with rgba colors
export const hexToRgba = (hex: string, alpha = 1): RGBAColor => {
  let r = 0, g = 0, b = 0;
  
  // 3 digits
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } 
  // 6 digits
  else if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  }
  
  return { r, g, b, a: alpha };
};

export const rgbaToHex = (rgba: RGBAColor): string => {
  const toHex = (value: number) => {
    const hex = Math.round(value).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(rgba.r)}${toHex(rgba.g)}${toHex(rgba.b)}`;
};

export const rgbaToString = (rgba: RGBAColor): string => {
  return `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`;
};

export const stringToRgba = (colorString: string): RGBAColor => {
  // Handle hex color
  if (colorString.startsWith('#')) {
    return hexToRgba(colorString);
  }
  
  // Handle rgba string
  if (colorString.startsWith('rgba')) {
    const matches = colorString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (matches) {
      return {
        r: parseInt(matches[1], 10),
        g: parseInt(matches[2], 10),
        b: parseInt(matches[3], 10),
        a: matches[4] !== undefined ? parseFloat(matches[4]) : 1
      };
    }
  }
  
  // Handle rgb string
  if (colorString.startsWith('rgb')) {
    const matches = colorString.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (matches) {
      return {
        r: parseInt(matches[1], 10),
        g: parseInt(matches[2], 10),
        b: parseInt(matches[3], 10),
        a: 1
      };
    }
  }
  
  // Default fallback
  return { r: 0, g: 0, b: 0, a: 1 };
};
